import React from "react";
import { DRAW_COUNT, TOTAL_NUMBERS } from "../../utils/bondDrawHelpers";

const NUMBERS = Array.from({ length: TOTAL_NUMBERS }, (_, i) => i + 1);
const GRID_COLS = 6;

const BondDrawNumberGrid = ({
  selected = [],
  drawn = [],
  revealedDrawnCount = null,
  disabled = false,
  maxSelection = 6,
  isDrawing = false,
  drawPhase = "idle",
  drawnTotal = DRAW_COUNT,
  onToggleNumber,
}) => {
  const selectedSet = new Set(selected);
  const visibleDrawn =
    revealedDrawnCount != null ? drawn.slice(0, Math.max(0, revealedDrawnCount)) : drawn;
  const drawnSet = new Set(visibleDrawn);
  const drawnLatest = visibleDrawn.length > 0 ? visibleDrawn[visibleDrawn.length - 1] : null;
  const isShuffling = isDrawing && drawPhase === "shuffling";
  const isRevealing = isDrawing && drawPhase === "revealing";
  const progressTotal = Math.max(drawn.length || drawnTotal || DRAW_COUNT, 1);
  const progressCount = isRevealing ? visibleDrawn.length : 0;

  const handleClick = (n) => {
    if (disabled || !onToggleNumber) return;
    if (selectedSet.has(n)) {
      onToggleNumber(selected.filter((x) => x !== n));
    } else if (selected.length < maxSelection) {
      onToggleNumber([...selected, n]);
    }
  };

  const selectedCount = selected.length;

  return (
    <div
      className={`bdraw-grid-stage${isDrawing ? " is-drawing" : ""}${isShuffling ? " is-shuffling" : ""}${isRevealing ? " is-revealing" : ""}${drawPhase === "result" ? " is-result" : ""}`}
    >
      {!isDrawing ? (
        <div className="bdraw-grid-head">
          <span className="bdraw-grid-head-title">
            <span className="bdraw-grid-head-orb" aria-hidden />
            Bond Draw · 1–{TOTAL_NUMBERS}
          </span>
          <span className="bdraw-grid-head-pills" aria-hidden>
            {Array.from({ length: maxSelection }, (_, i) => (
              <span key={`hp-${i}`} className={`bdraw-grid-head-pill${i < selectedCount ? " on" : ""}`} />
            ))}
          </span>
          <span className="bdraw-grid-head-count">
            {selectedCount > 0 ? `${selectedCount}/${maxSelection}` : `Pick ${maxSelection}`}
          </span>
        </div>
      ) : null}

      {isDrawing ? (
        <div className="bdraw-draw-progress" aria-live="polite">
          {isShuffling ? (
            <span className="bdraw-draw-progress-label">Shuffling numbers…</span>
          ) : (
            <>
              <span className="bdraw-draw-progress-label">
                Ball {Math.max(progressCount, 1)} of {progressTotal}
              </span>
              <div className="bdraw-draw-progress-dots" aria-hidden>
                {Array.from({ length: progressTotal }, (_, index) => (
                  <span
                    key={`ball-${index}`}
                    className={`bdraw-draw-dot${index < progressCount ? " on" : ""}${index === progressCount && isRevealing ? " active" : ""}`}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      ) : null}

      <div className={`bdraw-grid${isDrawing ? " is-drawing" : ""}`} role="grid" aria-label="Bond Draw number grid">
        {NUMBERS.map((n) => {
          const row = Math.floor((n - 1) / GRID_COLS);
          const col = (n - 1) % GRID_COLS;
          const isSelected = selectedSet.has(n);
          const isDrawn = drawnSet.has(n);
          const isMatch = isSelected && isDrawn;
          const isLatest = drawnLatest === n;

          let className = "bdraw-cell";
          if (isMatch) className += " match";
          else if (isDrawn) className += " drawn";
          else if (isSelected) className += " selected";
          if (isLatest) className += " latest";
          if (isShuffling) className += " shuffling";

          return (
            <button
              key={n}
              type="button"
              className={className}
              style={{ "--cell-delay": `${row * 68 + col * 34}ms` }}
              disabled={disabled}
              onClick={() => handleClick(n)}
              aria-pressed={isSelected}
              aria-label={`Number ${n}${isMatch ? " — match" : isDrawn ? " — drawn" : isSelected ? " — selected" : ""}`}
            >
              <span className="bdraw-cell-num">{n}</span>
              {isLatest ? <span className="bdraw-cell-ring" aria-hidden /> : null}
              {isMatch && isLatest ? <span className="bdraw-cell-spark" aria-hidden /> : null}
              {isMatch ? <span className="bdraw-cell-badge" aria-hidden /> : null}
            </button>
          );
        })}
      </div>

      {isDrawing ? <div className="bdraw-draw-glow" aria-hidden /> : null}
    </div>
  );
};

export default BondDrawNumberGrid;
